import { getApp, getApps } from 'firebase/app';
import { getAuth } from 'firebase/auth';

const configuredApiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/+$/, '');
const API_URL = configuredApiUrl.endsWith('/api') ? configuredApiUrl : `${configuredApiUrl}/api`;

// Session tokens are never stored in localStorage/JS-readable state: the
// server sets them as httpOnly cookies (see server/src/routes/auth.routes.ts)
// and the browser attaches them automatically. Firebase-authenticated
// (Google) requests instead attach a fresh ID token from the Firebase SDK,
// which persists its own session securely and is not stored by this app.
const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

// The CSRF cookie is created and cleared with the cookie-authenticated
// session. Its value is not an access credential, but its presence lets the
// application avoid an expected unauthenticated `/auth/me` request on first
// load for visitors who are not signed in.
export const hasWebSession = (): boolean => Boolean(getCookie('csrfToken'));

// Ensures that an expired session or a cookie-domain configuration change
// cannot leave the sign-in form without the double-submit CSRF value.
export async function refreshCsrfToken(): Promise<boolean> {
  const response = await apiFetch<{ data: { hasSession: boolean } }>('/auth/csrf');
  return response.data.hasSession;
}

const getFirebaseIdToken = async (): Promise<string | null> => {
  try {
    // Do not initialize Firebase here. `getFirebaseAuth` obtains its public
    // configuration through this API client, so initializing it would make
    // the configuration request wait on itself and leave the app loading.
    if (!getApps().length) return null;
    return (await getAuth(getApp()).currentUser?.getIdToken()) || null;
  } catch {
    return null;
  }
};

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const firebaseToken = await getFirebaseIdToken();
  if (firebaseToken) headers.set('Authorization', `Bearer ${firebaseToken}`);
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const method = (init.method || 'GET').toUpperCase();
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrfToken = getCookie('csrfToken');
    if (csrfToken) headers.set('X-CSRF-Token', csrfToken);
  }
  const response = await fetch(`${API_URL}${path}`, { ...init, headers, credentials: 'include' });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body.success === false) throw new Error(body.error || body.message || 'Request failed');
  return body as T;
}

export async function uploadImage(file: File, kind: 'profile' | 'listing' = 'listing'): Promise<string> {
  const form = new FormData(); form.append('image', file); form.append('kind', kind);
  const response = await apiFetch<{ data: { url: string } }>('/uploads/image', { method: 'POST', body: form });
  return response.data.url;
}
